/**
 * renderMarkdownLinks — turns Admin/News-style `[link text](https://url)`
 * inline links (and paragraph breaks) into React nodes.
 *
 * Mirrors the parser used in `pages/News.jsx` so Admin editors have a
 * single mental model across News, Recipe "About this recipe" summaries,
 * and Kitchen Help definitions — supporting cross-linking for SEO
 * without needing a full rich-text editor.
 *
 * Safety:
 *   - Only `http://`, `https://`, or same-origin absolute paths (`/...`)
 *     are honoured; `javascript:` / `data:` URLs render as plain text.
 *   - External links open with `target=_blank rel=noreferrer noopener`.
 *   - Internal links (starting with `/`) open in the same tab.
 *
 * Options:
 *   { splitParagraphs?: boolean }   default true — wraps each `\n\n` block in <p>
 *   { paragraphClass?: string }     className for each <p> when splitting
 *   { linkClass?: string }          className for each <a>
 */
import { Fragment } from "react";

const LINK_RE = /\[([^\]]+)\]\(([^)]+)\)/g;
const SAFE_HREF_RE = /^(https?:\/\/|\/)/i;

function renderInline(text, keyPrefix, linkClass) {
  const nodes = [];
  let last = 0;
  let m;
  LINK_RE.lastIndex = 0;
  while ((m = LINK_RE.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const label = m[1];
    const href = m[2].trim();
    if (SAFE_HREF_RE.test(href)) {
      const external = /^https?:/i.test(href);
      nodes.push(
        <a
          key={`${keyPrefix}-${m.index}`}
          href={href}
          className={linkClass}
          {...(external ? { target: "_blank", rel: "noreferrer noopener" } : {})}
        >
          {label}
        </a>
      );
    } else {
      // Unsafe scheme — render the literal markdown so admins can see + fix it.
      nodes.push(m[0]);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes.length ? nodes : [text];
}

export function renderMarkdownLinks(
  text,
  { splitParagraphs = true, paragraphClass = "", linkClass = "text-rf-orange font-bold underline" } = {}
) {
  const src = text || "";
  if (!splitParagraphs) {
    return <Fragment>{renderInline(src, "il", linkClass)}</Fragment>;
  }
  const paragraphs = src.split(/\n{2,}/);
  return (
    <Fragment>
      {paragraphs.map((para, i) => (
        <p key={i} className={paragraphClass}>
          {renderInline(para, `p${i}`, linkClass)}
        </p>
      ))}
    </Fragment>
  );
}

export default renderMarkdownLinks;
